import type { AttackerData } from '../entities/Attacker';
import type { BoardSystem } from './BoardSystem';
import type { QueueSystem } from './QueueSystem';
import type { StackSystem } from './StackSystem';
import { computeColorReachability, type ReachabilityOptions } from './Reachability';
import { cellKey, type CellStack } from '../types/board';

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Build ReachabilityOptions from a live BoardSystem's special cells.
 */
function buildReachabilityOptions(board: BoardSystem): ReachabilityOptions {
  const options: ReachabilityOptions = {};

  // Snake cells
  const snakeCells = board.getSnakeCells();
  if (snakeCells.size > 0) {
    options.snakeCells = new Set(snakeCells);
  }

  // Mega blocks
  const megaBlocks = board.getMegaBlocks();
  if (megaBlocks.size > 0) {
    options.megaBlockDefs = [];
    for (const [, mb] of megaBlocks) {
      options.megaBlockDefs.push({
        row: mb.row,
        col: mb.col,
        width: mb.width,
        height: mb.height,
        colorIndex: mb.colorIndex,
        hp: mb.hp,
      });
    }
  }

  // Door cells
  const doors = board.getActiveDoors();
  if (doors.size > 0) {
    const doorCellSet = new Set<number>();
    for (const [, door] of doors) {
      for (let r = door.row; r < door.row + door.height; r++) {
        for (let c = door.col; c < door.col + door.width; c++) {
          doorCellSet.add(cellKey(r, c));
        }
      }
    }
    options.doorCells = doorCellSet;
  }

  // Barricade cells
  const barricades = board.getActiveBarricades();
  if (barricades.length > 0) {
    const barricadeCellSet = new Set<number>();
    for (const b of barricades) {
      for (let r = b.row; r < b.row + b.height; r++) {
        for (let c = b.col; c < b.col + b.width; c++) {
          barricadeCellSet.add(cellKey(r, c));
        }
      }
    }
    options.barricadeCells = barricadeCellSet;
  }

  // Blockade walls
  const blockadeWalls = board.getActiveBlockadeWalls();
  if (blockadeWalls.size > 0) {
    options.blockadeWalls = [];
    for (const [, wall] of blockadeWalls) {
      options.blockadeWalls.push({
        side: wall.side,
        hp: wall.hp,
        startCell: wall.startCell,
        length: wall.length,
      });
    }
  }

  return options;
}

/**
 * Check if a single attacker can make any progress against the given
 * set of reachable colors.
 *
 * An attacker "can make progress" if at least one of its orbs has
 * ammo > 0 AND that orb's color is in the reachable set.
 */
function attackerCanProgress(
  attacker: AttackerData,
  reachableColors: ReadonlySet<number>,
): boolean {
  if (attacker.isHammer) return false; // hammers don't shoot cubes
  for (const orb of attacker.orbs) {
    if (orb.ammo > 0 && reachableColors.has(orb.colorIndex)) {
      return true;
    }
  }
  return false;
}

// ── Soft Deadlock Detection ──────────────────────────────────────────

/**
 * Checks if the current game state has no possible progress remaining.
 *
 * This is a "soft deadlock" check — it fires when NO attacker (on the
 * conveyor, in the stack, or in the queue) can knock out any more
 * reachable cells. Unlike the old implementation which ran a full
 * simulation, this simply checks whether any attacker has ammo for a
 * color that still has reachable cells on the board.
 *
 * @returns true if no progress is possible (soft deadlock detected)
 */
export function isSoftDeadlocked(
  board: BoardSystem,
  conveyorAttackers: Iterable<AttackerData>,
  stack: StackSystem,
  queue: QueueSystem,
): boolean {
  // If the board is already cleared, no deadlock (game is won)
  if (board.isCleared()) return false;

  // Compute which colors have reachable cells
  // Cast: getGrid() returns deeply-readonly, but computeColorReachability only reads.
  const grid = board.getGrid() as CellStack[][];
  const reachabilityOptions = buildReachabilityOptions(board);
  const colorReachability = computeColorReachability(grid, reachabilityOptions);

  // Build the set of colors that have at least 1 reachable cell
  const reachableColors = new Set<number>();
  for (const [color, info] of colorReachability) {
    if (info.reachable > 0) {
      reachableColors.add(color);
    }
  }

  // If no colors are reachable but board isn't cleared, all cells are buried.
  // That's a deadlock — no one can hit anything.
  if (reachableColors.size === 0) return true;

  // Check conveyor attackers
  for (const attacker of conveyorAttackers) {
    if (attackerCanProgress(attacker, reachableColors)) return false;
  }

  // Check stack attackers
  for (const slot of stack.getSlots()) {
    if (!slot) continue;
    if (attackerCanProgress(slot, reachableColors)) return false;
  }

  // Check queue attackers (de-duplicate multi-orb spanning multiple lanes)
  const allLanes = queue.getAllQueues();
  const seen = new Set<number>();
  for (const lane of allLanes) {
    for (const attacker of lane) {
      if (!attacker) continue;
      if (seen.has(attacker.id)) continue;
      seen.add(attacker.id);
      if (attackerCanProgress(attacker, reachableColors)) return false;
    }
  }

  // No attacker anywhere can make progress → deadlocked
  return true;
}

// ── Debounced Checker Class ──────────────────────────────────────────

/**
 * Manages soft deadlock detection with debounce/cooldown to avoid running
 * the reachability analysis too frequently.
 */
export class SoftDeadlockChecker {
  private lastCheckTime = 0;
  private readonly cooldownMs: number;

  constructor(cooldownMs: number = 1000) {
    this.cooldownMs = cooldownMs;
  }

  /**
   * Check for soft deadlock if enough time has elapsed since the last check.
   * Returns true if soft deadlock is detected, false otherwise.
   * Returns false (safe) if the cooldown hasn't elapsed yet.
   */
  check(
    board: BoardSystem,
    conveyorAttackers: Iterable<AttackerData>,
    stack: StackSystem,
    queue: QueueSystem,
  ): boolean {
    const now = Date.now();
    if (now - this.lastCheckTime < this.cooldownMs) return false;
    this.lastCheckTime = now;

    return isSoftDeadlocked(board, conveyorAttackers, stack, queue);
  }

  /** Reset the cooldown timer (e.g., on level start). */
  reset(): void {
    this.lastCheckTime = 0;
  }
}
